//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
// feed-scheduler imports
import * as s from "./RangeAndDurationPicker.styles";
import { IContainer, IProps, IState } from "./RangeAndDurationPickerTypes";
// other imports
// import { UserImage } from 'src/components/user-image';
import { DurationPicker } from "#webapp/src/components/duration-picker";
import { ScheduleRangePicker } from "#webapp/src/components/schedule-range-picker";
// state
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
// util
import { track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { EScheduleRange } from "#webapp/src/util/scheduler.util";
// material-ui
import { AccessTime } from "@clockwise/design-system/icons";
import { DatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";

//////////////////
// COMPONENT
//////////////////
class RangeAndDurationPickerCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      minutesDuration: props.minutesDuration || 30,
      range: props.range || EScheduleRange.NextSeven,
      specifiedDate: props.specifiedDate || null,
      showDatePicker: false,
      durationInputDialogOpen: false,
      durationInputDialogValue: "",
      durationInputDialogError: "",
    };
  }

  public componentDidUpdate() {
    const { showDatePicker } = this.state;
    const datePickerEl = document.getElementById("event-scheduler-date-picker");
    // hacky way to get date-picker to autofocus because there is no real way to do it via props..
    if (showDatePicker && datePickerEl) {
      datePickerEl.click();
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { minutesDuration, range, specifiedDate } = nextProps;
    const newState: Partial<IState> = Object.assign(
      {},
      minutesDuration && minutesDuration !== this.state.minutesDuration ? { minutesDuration } : {},
      range && range !== this.state.range ? { range } : {},
      specifiedDate && specifiedDate !== this.state.specifiedDate ? { specifiedDate } : {},
    );
    this.setState(newState as IState);
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onDurationChange = (minutesDuration: number) => {
    track(TrackingEvents.SCHEDULING.EVENT_SCHEDULER_TIME_INPUT_DURATION_CHANGED, {
      minutesDuration,
      schedulingSessionId: this.props.schedulingSessionId,
    }); // analytics

    this.setState({ minutesDuration });

    if (this.props.onChange) {
      this.props.onChange(minutesDuration, this.state.range, this.state.specifiedDate);
    }
  };

  private onDateChange = (muiDate: MaterialUiPickersDate) => {
    const date = muiDate && muiDate.toDate();
    this.setState({ showDatePicker: false, specifiedDate: date });
    if (this.props.onChange) {
      this.props.onChange(this.state.minutesDuration, this.state.range, date);
    }
  };

  private onDatePickerClose = () => {
    setTimeout(() => {
      // dumb date-picker library doesnt have some sort of onCancel so onChange and onClose fire at
      // the same time with no way of telling if the picker closed without selecting a date :(
      const { range, specifiedDate } = this.state;
      if (
        (range === EScheduleRange.AroundDate || range === EScheduleRange.SpecificDate) &&
        !specifiedDate
      ) {
        this.setState({
          showDatePicker: false,
          specifiedDate: null,
          range: EScheduleRange.NextSeven,
        });
      }
    }, 250);
  };

  private onRangeChange = (range: EScheduleRange) => {
    if (!range) {
      return;
    }

    track(TrackingEvents.SCHEDULING.EVENT_SCHEDULER_TIME_INPUT_RANGE_CHANGE, {
      range,
      schedulingSessionId: this.props.schedulingSessionId,
    }); // analytics
    if (range === EScheduleRange.SpecificDate || range === EScheduleRange.AroundDate) {
      this.setState({ range, showDatePicker: true });
    } else {
      this.setState({ range, specifiedDate: null });
    }
    if (this.props.onChange) {
      this.props.onChange(this.state.minutesDuration, range, null);
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  public render() {
    const { minutesDuration, specifiedDate, range } = this.state;
    const durations = [15, 30, 45, 60, 90, 120];

    return (
      <div className="cw-flex cw-gap-2 cw-max-w-full cw-min-w-0 cw-items-center cw-border-b-1 cw-border-0 cw-border-solid cw-border-b-grey-lightest cw-py-1">
        <AccessTime className="cw-h-4 cw-w-4 cw-text-slate-standard" />
        <div className="cw-flex cw-gap-2 cw-flex-grow cw-min-w-0">
          <div className="cw-max-w-[100px]">
            <DurationPicker
              onDurationChange={this.onDurationChange}
              durations={durations}
              defaultDuration={minutesDuration}
              maxDuration={240}
              disabled={!!this.props.isReschedulerEvent}
              cw-id="duration-picker"
            />
          </div>
          {this.state.showDatePicker && (
            <DatePicker
              variant="inline"
              value={specifiedDate}
              onChange={this.onDateChange}
              onClose={this.onDatePickerClose}
              disablePast
              autoOk
              disableToolbar
              fullWidth
              id="event-scheduler-date-picker"
              style={{ width: 0 }}
            />
          )}
          <ScheduleRangePicker
            selectedDateLabel={
              (range === EScheduleRange.SpecificDate &&
                specifiedDate &&
                `${specifiedDate.getMonth() + 1}/${specifiedDate.getDate()}`) ||
              undefined
            }
            selectedAroundDateLabel={
              (range === EScheduleRange.AroundDate &&
                specifiedDate &&
                `${specifiedDate.getMonth() + 1}/${specifiedDate.getDate()}`) ||
              undefined
            }
            feedScheduler
            style={s.dateRangePicker}
            labelStyle={{ paddingRight: 35 }}
            underlineStyle={s.underlineStyle}
            selectedRange={range}
            onChange={this.onRangeChange}
            hideIcon
            cw-id="range-picker"
          />
        </div>
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    schedulingSessionId: state.feedScheduler.schedulingSessionId,
  };
}

export const RangeAndDurationPicker = connect(mapStateToProps, {})(RangeAndDurationPickerCmpt);
